import React, { useEffect, useRef, useState } from 'react';
import { photosShow } from '../../helpers/photosShow';
import fetch from '../../core/fetch';
import { homebanneruploadDir } from '../../config';
import { toWebp } from '../../helpers/imageHelper';

/**
 * MarketingBannerPopup - Ad Server Professional v2
 * World Class UI: Glassmorphism, Spring Animations, Premium Design
 * Features: CPC/CPM Tracking, Weighted Rotation
 */
const MarketingBannerPopup = ({ show, onHide, banner }) => {
  const hasTrackedView = useRef(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  
  // Animation delay for mount
  useEffect(() => {
    if (show) {
      // Small delay for smooth entry animation
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [show]);
  
  // Track view when popup is shown (only once per session)
  useEffect(() => {
    if (show && banner?.id && !hasTrackedView.current) {
      const sessionKey = `ad_viewed_${banner.id}`;
      if (typeof window !== 'undefined' && !sessionStorage.getItem(sessionKey)) {
        trackView(banner.id);
        sessionStorage.setItem(sessionKey, 'true');
        hasTrackedView.current = true;
      }
    }
  }, [show, banner?.id]);
  
  const trackView = async (bannerId) => {
    try {
      await fetch(`/web-api/ads/track-view/${bannerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
    } catch (error) {
      console.error('[AdServer] Error tracking view:', error);
    }
  };
  
  const handleClick = async () => {
    if (banner?.id) {
      try {
        await fetch(`/web-api/ads/track-click/${banner.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });
      } catch (error) {
        console.error('[AdServer] Error tracking click:', error);
      }
    }
    window.location.href = banner.link;
  };
  
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onHide();
    }, 300);
  };
  
  if (!show || !banner) return null;
  
  const imagePath = photosShow(homebanneruploadDir) + toWebp(banner.image);

  // ═══════════════════════════════════════════════════════════════════
  // WORLD CLASS UI STYLES - Glassmorphism + Premium Design
  // ═══════════════════════════════════════════════════════════════════
  
  const styles = {
    // Backdrop with blur effect
    backdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      opacity: isVisible && !isClosing ? 1 : 0,
      transition: 'opacity 0.3s ease-out',
      padding: '20px',
    },
    
    // Main card container
    card: {
      position: 'relative',
      width: '100%',
      maxWidth: '480px',
      borderRadius: '24px',
      overflow: 'hidden',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
      transform: isVisible && !isClosing ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(20px)',
      transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease-out',
      opacity: isVisible && !isClosing ? 1 : 0,
    },
    
    // Image container
    imageContainer: {
      position: 'relative',
      width: '100%',
      aspectRatio: '16 / 10',
      backgroundImage: `url(${imagePath})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    },
    
    // Gradient overlay
    overlay: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
      padding: '60px 24px 24px',
    },
    
    // Close button - Premium circular design
    closeButton: {
      position: 'absolute',
      top: '16px',
      right: '16px',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      zIndex: 10,
    },
    
    closeIcon: {
      color: 'white',
      fontSize: '20px',
      fontWeight: '300',
      lineHeight: 1,
    },
    
    // Title
    title: {
      color: 'white',
      fontSize: '24px',
      fontWeight: '700',
      margin: '0 0 16px 0',
      lineHeight: '1.3',
      textShadow: '0 2px 10px rgba(0,0,0,0.3)',
    },
    
    // CTA Button - Premium gradient with hover effect
    ctaButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '14px 32px',
      background: 'linear-gradient(135deg, #0056b3 0%, #003d82 100%)',
      color: 'white',
      fontSize: '16px',
      fontWeight: '600',
      borderRadius: '50px',
      border: 'none',
      cursor: 'pointer',
      boxShadow: '0 4px 15px rgba(0, 86, 179, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
      transition: 'all 0.3s ease',
      textDecoration: 'none',
    },
  };

  return (
    <div style={styles.backdrop} onClick={handleClose}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <div 
          style={styles.closeButton}
          onClick={handleClose}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <span style={styles.closeIcon}>✕</span>
        </div>
        
        {/* Image with overlay */}
        <div style={styles.imageContainer}>
          <div style={styles.overlay}>
            <h2 style={styles.title}>{banner.title}</h2>
            <button 
              style={styles.ctaButton}
              onClick={handleClick}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 86, 179, 0.5), inset 0 1px 0 rgba(255,255,255,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 86, 179, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)';
              }}
            >
              {banner.buttonText}
              <span style={{ marginLeft: '8px' }}>→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingBannerPopup; 